import { type RefObject, useLayoutEffect } from 'react'
import { sigMs, signatureMoves } from './motion'
import { writeTitle } from './pen'
import { writeSectionRules } from './rules'

/**
 * The navigation signature (DESIGN.md "The signature layer"): once per
 * navigation the screen's title writes itself and the rules on screen
 * follow. Never on a re-render, and never on a state change inside a screen.
 *
 * One trigger serves every way of changing screen: the app's router calls
 * `runNavigationSignature` after the new screen has rendered and before it
 * paints. A title that mounts a little later (its screen was still loading)
 * claims the same navigation once through `useTitleSignature`.
 */

const TITLE = '[data-slot="page-title"]'
/** How long after a navigation a late title may still claim it. */
const CLAIM_WINDOW = 3000
/** How long a title waits for its display face before it is left as it is. */
const FACE_WAIT = 600

let navigation = 0
let navigatedAt = Number.NEGATIVE_INFINITY
let written = 0

/** Whether the faces this title is set in are loaded. */
function faceReady(title: HTMLElement): boolean {
  const fonts = document.fonts
  if (!fonts || typeof fonts.check !== 'function') return true
  const cs = getComputedStyle(title)
  try {
    return fonts.check(`${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`, title.textContent ?? '')
  } catch {
    return fonts.status === 'loaded'
  }
}

function sign(title: HTMLElement | null, root: ParentNode) {
  const current = navigation
  if (!signatureMoves()) return
  if (!title) {
    writeSectionRules(root, current, 0)
    return
  }
  written = current
  const satr = title.closest('[data-satr]') ?? undefined
  const write = () => {
    if (!title.isConnected || navigation !== current) return
    writeTitle(title)
    writeSectionRules(root, current, sigMs('--sig-delay-sections', 200), satr)
  }
  if (faceReady(title)) {
    write()
    return
  }
  // The display face is not here yet: write only if it arrives in time, and
  // meanwhile leave the title and its rules complete.
  const asked = performance.now()
  void document.fonts.ready.then(() => {
    if (performance.now() - asked <= FACE_WAIT) write()
  })
}

/**
 * Start a navigation's signature over `root` (the app's page column). Call
 * it once per path change, after the new screen rendered and before paint.
 */
export function runNavigationSignature(root: ParentNode = document) {
  navigation += 1
  navigatedAt = performance.now()
  sign(root.querySelector<HTMLElement>(TITLE), root)
}

/**
 * A screen title's claim on the current navigation: when the title mounts
 * after the router's signature already ran (its screen was still loading),
 * it writes itself once. Re-renders and later remounts do nothing.
 */
export function useTitleSignature(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const title = ref.current
    if (!title || navigation === 0 || written === navigation) return
    if (performance.now() - navigatedAt > CLAIM_WINDOW) return
    sign(title, title.closest('main') ?? document)
  }, [ref])
}
